import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { toast } from "sonner";
import { Check, Lock, Loader2, UserRound, CreditCard, Gift } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Reassurance, CardLogos, TermsConsent } from "@/components/PaymentTrust";
import { Btn, BtnLink, Card, Eyebrow } from "@/components/kit";
import { FullLoader, useMeQuery } from "@/components/auth/AccessGate";
import { useApi } from "@/lib/api";

const INCLUDED = [
  "Les 7 missions guidées, de l'idée à la vidéo",
  "Ton script personnalisé, imprimable et à répéter avec le minuteur",
  "Ton storyboard et tes checklists de tournage",
  "La bibliothèque de ressources et la FAQ",
  "Ta progression enregistrée dans ton compte, sur tous tes appareils",
];

function Step({ n, label, done, active, Icon }) {
  return (
    <li className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${active ? "border-bx/30 bg-white" : "border-line bg-white/60"}`}>
      <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${done ? "bg-bx text-white" : active ? "border-2 border-bx text-bx" : "border border-line text-soft"}`}>
        {done ? <Check className="h-4 w-4" strokeWidth={3} /> : n}
      </span>
      <Icon className={`h-4 w-4 ${active || done ? "text-bx" : "text-soft"}`} strokeWidth={1.6} />
      <span className={done || active ? "text-ink" : "text-soft"}>{label}</span>
    </li>
  );
}

function RedeemGift({ isSignedIn }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api("POST", "/gifts/redeem", { code });
      toast.success("Code cadeau accepté : ton parcours est débloqué !");
      window.location.assign("/mission/1");
    } catch (err) {
      setBusy(false);
      toast.error(err.message || "Ce code n'a pas pu être utilisé.");
    }
  };
  return (
    <div className="mt-5 rounded-2xl border border-gold/40 bg-ivory/70 px-4 py-3" data-testid="redeem-gift">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center gap-2 text-left text-sm font-medium text-ink" data-testid="redeem-gift-toggle">
        <Gift className="h-4 w-4 text-bx" strokeWidth={1.6} />Tu as reçu un code cadeau ?
      </button>
      {open && (isSignedIn ? (
        <form onSubmit={submit} className="mt-3 flex flex-col gap-2 sm:flex-row">
          <label htmlFor="gift-code" className="sr-only">Code cadeau</label>
          <input id="gift-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="UGCGO-XXXX-XXXX" autoComplete="off"
            className="min-w-0 flex-1 rounded-xl border border-line bg-white px-4 py-2.5 text-base uppercase tracking-wider outline-none focus:border-bx focus:ring-2 focus:ring-bx/20" data-testid="redeem-gift-input" />
          <Btn type="submit" disabled={busy || code.trim().length < 6} data-testid="redeem-gift-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Utiliser</Btn>
        </form>
      ) : (
        <p className="mt-2 text-sm text-soft" data-testid="redeem-gift-signin-hint">Crée d'abord ton compte (ou connecte-toi), puis reviens ici pour saisir ton code.</p>
      ))}
    </div>
  );
}

export default function Access() {
  const { isLoaded, isSignedIn, me, error } = useMeQuery();
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [terms, setTerms] = useState(false);

  if (!isLoaded || (isSignedIn && !me && !error)) return <FullLoader />;
  if (me?.has_access) return <Navigate to="/parcours" replace />;

  const pay = async () => {
    setLoading(true);
    try {
      const { checkout_url } = await api("POST", "/payments/checkout", { origin_url: window.location.origin, accepted_terms: terms });
      window.location.href = checkout_url;
    } catch (e) {
      setLoading(false);
      toast.error(e.status === 409 ? "Ton accès est déjà débloqué." : "Le paiement n'a pas pu démarrer. Réessaie dans un instant.");
    }
  };

  return (
    <div className="min-h-screen bg-ivory" data-testid="access-page">
      <header className="mx-auto flex max-w-5xl items-center justify-between px-5 py-5 sm:px-8">
        <Logo />
        {isSignedIn ? <UserButton /> : <BtnLink to="/sign-in" variant="outline" size="sm" data-testid="access-sign-in-link">Se connecter</BtnLink>}
      </header>
      <main className="mx-auto grid max-w-5xl gap-8 px-5 pb-20 pt-6 sm:px-8 lg:grid-cols-[1.2fr_1fr] lg:pt-12">
        <section className="animate-fade-up">
          <Eyebrow>Ma première vidéo UGC</Eyebrow>
          <h1 className="mt-4 text-4xl font-semibold leading-tight text-bx sm:text-5xl">Débloque ton parcours</h1>
          <p className="mt-4 max-w-lg text-lg text-soft">Un accès complet au parcours, pour créer ta première vidéo UGC d'entraînement à ton rythme.</p>
          <ul className="mt-8 space-y-3">
            {INCLUDED.map((t) => (
              <li key={t} className="flex gap-3 text-ink"><span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bx/[0.08] text-bx"><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>{t}</li>
            ))}
          </ul>
          <p className="mt-8 text-sm text-soft">UGC GO ne promet ni collaboration rémunérée, ni revenu.</p>
        </section>
        <Card className="h-fit lg:sticky lg:top-8" data-testid="access-offer-card">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-soft">Accès complet</p>
          <p className="mt-3 flex items-baseline gap-2"><span className="font-display text-6xl text-bx" data-testid="access-price">9 €</span><span className="text-soft">TTC</span></p>
          <p className="mt-1 text-sm font-medium text-bx-2">Paiement unique, sans abonnement</p>
          <ol className="mt-6 space-y-2.5">
            <Step n={1} label="Je crée mon compte" done={isSignedIn} active={!isSignedIn} Icon={UserRound} />
            <Step n={2} label="Je règle 9 € une seule fois" active={isSignedIn} Icon={CreditCard} />
          </ol>
          <div className="mt-6 space-y-3">
            {isSignedIn ? (
              <>
                <TermsConsent checked={terms} onChange={setTerms} testId="access-terms" />
                <Btn size="lg" className="w-full" onClick={pay} disabled={loading || !terms} data-testid="access-pay-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}Débloquer mon parcours · 9 €
                </Btn>
              </>
            ) : (
              <>
                <BtnLink to="/sign-up" size="lg" className="w-full" data-testid="access-sign-up-button">Créer mon compte</BtnLink>
                <BtnLink to="/sign-in" variant="outline" size="lg" className="w-full" data-testid="access-have-account-button">J'ai déjà un compte</BtnLink>
              </>
            )}
            {error && <p className="text-sm text-red-700">Impossible de vérifier ton compte. Actualise la page.</p>}
          </div>
          <Reassurance testId="access-reassurance" className="mt-4" />
          <CardLogos testId="access-card-logos" className="mt-3" />
          <p className="mt-5 flex items-center gap-2 text-xs text-soft"><Lock className="h-3.5 w-3.5" />Traité par Stripe. Aucune donnée bancaire n'est stockée par UGC GO.</p>
          <RedeemGift isSignedIn={isSignedIn} />
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <Link to="/" className="text-sm text-bx underline-offset-4 hover:underline" data-testid="access-back-home">Retour à l'accueil</Link>
            <Link to="/offrir" className="inline-flex items-center gap-1.5 text-sm text-bx underline-offset-4 hover:underline" data-testid="access-gift-link"><Gift className="h-4 w-4" strokeWidth={1.6} />Offrir le parcours</Link>
          </div>
        </Card>
      </main>
    </div>
  );
}
